import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ErrorHandlerService } from '../services/error-handler.service';

/**
 * Endpoints de autenticación donde un 401 es un error de credenciales legítimo
 * y NO debe disparar la redirección al login.
 * - /auth/login     → credenciales incorrectas del vendedor
 * - /auth/session/refresh → refresh token inválido (ya se maneja en TokenExpirationService)
 */
const AUTH_BYPASS_URLS = ['/auth/login', '/auth/session/refresh'];

/**
 * Interceptor de errores HTTP.
 *
 * Comportamiento ante 401 Unauthorized:
 *   - Si el 401 viene de un endpoint de autenticación (login / refresh):
 *     se deja pasar al handler normal — es un error de credenciales esperado.
 *   - En cualquier otro caso (ej: token revocado por apertura de 2ª sesión):
 *     se limpia el localStorage y se redirige al login, ya que el token
 *     de la sesión actual ya no es válido en el backend.
 *
 * Resto de errores: se delegan al ErrorHandlerService existente.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const errorHandler = inject(ErrorHandlerService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const esEndpointDeAuth = AUTH_BYPASS_URLS.some(url => req.url.includes(url));

        if (!esEndpointDeAuth) {
          // Token revocado (ej: sesión invalidada por 2ª login)
          // Limpiar estado local y forzar re-autenticación
          localStorage.clear();
          router.navigate(['/login']);
          return throwError(() => error);
        }
      }

      errorHandler.handleHttpError(error);
      return throwError(() => error);
    })
  );
};
